import gsap from 'gsap';
import Swiper from 'swiper';
import { EffectFade, Navigation, Pagination } from 'swiper/modules';
import { createApp, onMounted, onUnmounted, ref } from 'vue';

import { ANIMATION_EASES } from '~/constants/animations';
import * as DataDogLogs from '~/lib/datadog/datadog';

import { BaseSection } from './base-section';

/**
 * @type {import('swiper/types').SwiperOptions}
 */
const SWIPER_SETTINGS = {
  breakpoints: {
    768: {
      allowTouchMove: false,
    },
  },
  effect: 'fade',
  modules: [Navigation, EffectFade, Pagination],
  navigation: {
    nextEl: '.swiper-next-button',
    prevEl: '.swiper-prev-button',
  },
  pagination: {
    clickable: true,
    el: '.swiper-pagination',
  },
  speed: 1200,
};

/**
 * Social Proof Section
 *
 * This section shows testimonial quotes with animated circles and images.
 * The section implements an inView animation using GSAP when the section comes into view.
 *
 * @extends BaseSection
 * @constructor
 * @param {HTMLElement} container
 */
class SocialProof extends BaseSection {
  constructor() {
    super('[data-section-type="product-social-proof"]');
  }

  init(instanceData) {
    const container = instanceData.container;
    const enableLoop = container.dataset.enableLoop === 'true';
    const slidesCount = +container.dataset.slidesCount;

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Product Social Proof - init - Container not found',
        {},
        new Error('Container not found'),
      );
      return;
    }

    createApp({
      name: 'SocialProofSection',
      setup() {
        const carousel = ref(null);
        const swiperIsBeginning = ref(true);
        const swiperIsEnd = ref(false);

        const animateSlideContent = (slide) => {
          const content = slide?.querySelectorAll('.content');

          if (!content?.length) return;

          const tl = gsap.timeline();
          tl.fromTo(
            content,
            { opacity: 0 },
            {
              duration: 0.7,
              ease: ANIMATION_EASES.POWER3_IN_OUT,
              opacity: 1,
            },
            0.65,
          );
        };

        onMounted(() => {
          if (slidesCount <= 1) {
            DataDogLogs.warn(
              '[Allbirds section] - Product Social Proof - onMounted - Not enough slides to initialize Swiper',
              {},
              new Error('Not enough slides to initialize Swiper'),
            );
            return;
          }

          const swiperContainer = container.querySelector('.swiper');
          if (!swiperContainer) {
            DataDogLogs.error(
              '[Allbirds section] - Product Social Proof - onMounted - Swiper container not found',
              {},
              new Error('Swiper container not found'),
            );
            return;
          }

          if (enableLoop) {
            SWIPER_SETTINGS.loop = true;
          }

          SWIPER_SETTINGS.on = {
            realIndexChange: ({ activeIndex, isBeginning, isEnd, slides }) => {
              animateSlideContent(slides[activeIndex]);
              swiperIsBeginning.value = isBeginning;
              swiperIsEnd.value = isEnd;
            },
          };

          carousel.value = new Swiper(swiperContainer, SWIPER_SETTINGS);
        });

        onUnmounted(() => {
          carousel.value?.destroy();
          carousel.value = null;
        });

        return { swiperIsBeginning, swiperIsEnd };
      },
    }).mount(container);
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;

    if (!container) {
      return null;
    }

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        trigger: container,
      },
    });

    if (!tl) {
      return null;
    }

    tl.fromTo(
      container,
      { opacity: 0 },
      {
        duration: 0.3,
        opacity: 1,
      },
      0,
    );

    const line = container.querySelector('.line');
    if (line) {
      tl.fromTo(
        line,
        { scale: 0 },
        {
          duration: 2.2,
          ease: ANIMATION_EASES.POWER3_OUT,
          scale: 1,
        },
        0.3,
      );
    }

    const pill = container.querySelector('.pill');
    if (pill) {
      tl.fromTo(
        pill,
        {
          clipPath: 'inset(50% 50% 50% 50% round 400px)',
        },
        {
          clipPath: 'inset(0% 0% 0% 0% round 400px)',
          duration: 2,
          ease: ANIMATION_EASES.SINE_OUT,
        },
        0.8,
      );

      const borders = container.querySelectorAll('.pill-border');
      if (borders.length) {
        tl.fromTo(
          borders,
          { scale: 0 },
          {
            duration: 2,
            ease: ANIMATION_EASES.SINE_OUT,
            scale: 1,
          },
          0.8,
        );
      }
    }

    const content = container.querySelectorAll('.content');
    if (content.length) {
      tl.fromTo(
        content,
        { opacity: 0 },
        {
          duration: 1.5,
          ease: ANIMATION_EASES.POWER3_IN_OUT,
          opacity: 1,
        },
        1.5,
      );
    }

    const floatingImage = container.querySelector('.floating-img');
    if (floatingImage) {
      tl.fromTo(
        floatingImage,
        { opacity: 0, yPercent: 10 },
        {
          duration: 1.2,
          ease: ANIMATION_EASES.POWER3_OUT,
          opacity: 1,
          yPercent: 0,
        },
        2.5,
      );
    }

    const navigationButtons = container.querySelectorAll('.navigation-button');
    if (navigationButtons.length) {
      tl.fromTo(
        navigationButtons,
        { opacity: 0 },
        {
          duration: 0.8,
          opacity: 1,
        },
        2.5,
      );
    }

    const swiperPagination = container.querySelector('.swiper-pagination');
    if (swiperPagination) {
      tl.fromTo(
        swiperPagination,
        { opacity: 0 },
        {
          duration: 0.8,
          opacity: 1,
        },
        2.5,
      );
    }

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new SocialProof();
});
